import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux-toolkit/store";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import { getFilteredPositionIds } from "../../../db/DexieServises";

interface RoutesCoverageProps {
  selectedPositionIds?: (string | number)[];
}

const RoutesCoverage: React.FC<RoutesCoverageProps> = ({ selectedPositionIds }) => {
  const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([]);
  const [chartOptions, setChartOptions] = useState<any>({});
  const [monthsList, setMonthsList] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [monthModalOpen, setMonthModalOpen] = useState(false);
  const [monthRange, setMonthRange] = useState({
    from: { month: "", year: "" },
    to: { month: "", year: "" },
  });

  const positions = useSelector((state: RootState) => state.position.positions);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const monthNumberToName: { [key: number]: string } = {
    1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
    7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
  };

  const nameToMonthNumber: { [key: string]: number } = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
  };

  const toggleMonthModal = () => setMonthModalOpen((prev: boolean) => !prev);

  const handleApplyDateRange = () => {
    if (monthRange.from.month && monthRange.from.year && monthRange.to.month && monthRange.to.year) {
      const fromMonthNum = nameToMonthNumber[monthRange.from.month];
      const toMonthNum = nameToMonthNumber[monthRange.to.month];
      setDateRange({
        start: `${fromMonthNum}-${monthRange.from.year}`,
        end: `${toMonthNum}-${monthRange.to.year}`,
      });
    }
    toggleMonthModal();
  };

  const getMonthRangeButtonLabel = () => {
    if (dateRange.start && dateRange.end) {
      const [startMonth, startYear] = dateRange.start.split("-");
      const [endMonth, endYear] = dateRange.end.split("-");
      return `${monthNumberToName[parseInt(startMonth, 10)]} ${startYear} - ${monthNumberToName[parseInt(endMonth, 10)]} ${endYear}`;
    }
    return "Select Month Range";
  };

  /**
   * Fetch effort report for given position IDs and month
   */
  const fetchEffortReport = useCallback(
    async (positionIds: string, month: string) => {
      const serverUrl = localStorage.getItem("serverUrl");
      const url = `${serverUrl}${API.getEffortReport}`;

      try {
        const { status, data } = await getData(url, true, {
          position_id: positionIds,
          moye: month, // must be in yyyy-MM
        });

        if (status === 200 && Array.isArray(data.data)) {
          return data.data;
        }

        console.error("Failed to fetch effort report data");
        return [];
      } catch (error) {
        console.error("Error fetching effort report:", error);
        return [];
      }
    },
    []
  );

  /**
   * Prepare chart data
   */
  const processChartData = useCallback(async () => {
    if (!monthsList.length) return;

    // Use selectedPositionIds from props if provided, otherwise use Redux positions
    const positionIds =
      selectedPositionIds && selectedPositionIds.length > 0
        ? selectedPositionIds.join(",")
        : positions.length > 0
          ? positions.map((pos) => pos.id).join(",")
          : (await getFilteredPositionIds("Position", unitId)).join(",");

    const chartValues = {
      allRoutesPercent: [] as number[],
      uniqueRoutesPercent: [] as number[],
    };

    for (const month of monthsList) {
      const apiData = await fetchEffortReport(positionIds, month);

      const allRoutes = apiData.reduce(
        (sum: number, item: any) => sum + parseInt(item.PrimaryMet || "0", 10),
        0
      );
      const uniqueRoutes = apiData.reduce(
        (sum: number, item: any) => sum + parseInt(item.PrimaryVfMet || "0", 10),
        0
      );

      const total = allRoutes + uniqueRoutes;
      const allPercent = total ? (allRoutes / total) * 100 : 0;
      const uniquePercent = total ? (uniqueRoutes / total) * 100 : 0;

      chartValues.allRoutesPercent.push(allPercent);
      chartValues.uniqueRoutesPercent.push(uniquePercent);
    }

    setChartSeries([
      { name: "Covered Routes %", data: chartValues.allRoutesPercent },
      { name: "Unique Routes %", data: chartValues.uniqueRoutesPercent },
    ]);

    setChartOptions({
      chart: {
        type: "line",
        height: 400,
        toolbar: {
          tools: {
            download: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      // title: { text: "Routes Coverage (%)" },
      xaxis: {
        categories: monthsList,
        title: { text: "Month" },
      },
      yaxis: {
        title: { text: "Percentage" },
        min: 0,
        max: 100,
        labels: {
          formatter: (val: number) => `${val.toFixed(2)}%`,
        },
      },
      tooltip: {
        y: { formatter: (val: number) => `${val.toFixed(2)}%` },
      },
      colors: ["#ff9800", "#f44336"],
      legend: { position: "top" },
    });
  }, [positions, unitId, monthsList, fetchEffortReport, selectedPositionIds]);

  /**
   * Build months list strictly based on dateRange.start & dateRange.end
   */
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      const [startMonth, startYear] = dateRange.start.split("-").map(Number);
      const [endMonth, endYear] = dateRange.end.split("-").map(Number);

      const newMonths: string[] = [];

      for (let year = startYear; year <= endYear; year++) {
        const from = year === startYear ? startMonth : 1;
        const to = year === endYear ? endMonth : 12;

        for (let m = from; m <= to; m++) {
          newMonths.push(`${year}-${m.toString().padStart(2, "0")}`);
        }
      }

      setMonthsList(newMonths);
    } else {
      // Default to last 3 months if dateRange is not selected
      const months: string[] = [];
      const now = new Date();
      for (let i = 0; i < 3; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push(`${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`);
      }
      setMonthsList(months.reverse()); // Reverse to have oldest first
    }
  }, [dateRange]);

  /**
   * Refetch chart whenever months or positions change
   */
  useEffect(() => {
    setChartSeries([]);
    setChartOptions({});
    processChartData();
  }, [positions, monthsList, processChartData, selectedPositionIds]);

  return (
    <Col className="col-sm-12 col-xl-6 box-col-6">
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold fs-5">Routes Coverage (%)</h5>
            <Button color="info" onClick={toggleMonthModal} size="sm">
              {getMonthRangeButtonLabel()}
            </Button>
          </div>

          {/* Date Range Modal */}
          <Modal isOpen={monthModalOpen} toggle={toggleMonthModal} centered>
            <ModalHeader toggle={toggleMonthModal}>Select Month Range</ModalHeader>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <label>From</label>
                  <select
                    className="form-control"
                    value={`${monthRange.from.month}-${monthRange.from.year}`}
                    onChange={(e) => {
                      const [month, year] = e.target.value.split("-");
                      setMonthRange({ ...monthRange, from: { month, year } });
                    }}
                  >
                    <option value="">Select Month</option>
                    {yearOptions.flatMap((year) =>
                      Object.entries(monthNumberToName)
                        .sort(([a], [b]) => Number(b) - Number(a))
                        .filter(([monthNumber]) => {
                          if (year === currentYear) {
                            return Number(monthNumber) <= new Date().getMonth() + 1;
                          }
                          return true;
                        })
                        .map(([_, monthName]) => (
                          <option
                            key={`from-${monthName}-${year}`}
                            value={`${monthName}-${year}`}
                          >
                            {`${monthName}-${year}`}
                          </option>
                        ))
                    )}
                  </select>
                </Col>
                <Col md={6}>
                  <label>To</label>
                  <select
                    className="form-control"
                    value={`${monthRange.to.month}-${monthRange.to.year}`}
                    onChange={(e) => {
                      const [month, year] = e.target.value.split("-");
                      setMonthRange({ ...monthRange, to: { month, year } });
                    }}
                  >
                    <option value="">Select Month</option>
                    {yearOptions.flatMap((year) =>
                      Object.entries(monthNumberToName)
                        .sort(([a], [b]) => Number(b) - Number(a))
                        .filter(([monthNumber]) => {
                          if (year === currentYear) {
                            return Number(monthNumber) <= new Date().getMonth() + 1;
                          }
                          return true;
                        })
                        .map(([_, monthName]) => (
                          <option
                            key={`to-${monthName}-${year}`}
                            value={`${monthName}-${year}`}
                          >
                            {`${monthName}-${year}`}
                          </option>
                        ))
                    )}
                  </select>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleMonthModal}>
                Cancel
              </Button>
              <Button
                color="danger"
                onClick={() => {
                  setMonthRange({ from: { month: "", year: "" }, to: { month: "", year: "" } });
                  setDateRange({ start: "", end: "" });
                  toggleMonthModal();
                }}
              >
                Clear
              </Button>
              <Button color="success" onClick={handleApplyDateRange}>
                Apply
              </Button>
            </ModalFooter>
          </Modal>

          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={400}
          />
        </CardBody>
      </Card>
    </Col>
  );
};

export default RoutesCoverage;
